import { Controller } from "@hotwired/stimulus"
import { projectRange, dollars, roundedDollars } from "../lib/projection"

// /dca-calculator: the public DCA calculator. The page arrives already
// rendered from the query (DcaCalculator on the server); this keeps it live
// as the controls move, with the same maths (lib/projection.js mirrors
// app/models/projection.rb), and keeps the address bar in step
// (history.replaceState) so the result is a link that can be shared. The
// canonical stays the clean /dca-calculator either way (layout).
export default class extends Controller {
  static targets = [
    "amount", "years", "amountOut", "yearsOut", "yearsUnit",
    "mixGroup", "mixWords", "assetName",
    "mid", "low", "high", "contributed", "pct", "putInBar", "growthBar",
    "waitYears", "waitToday", "waitMid", "waitLost", "cta", "ctaNote"
  ]
  static values = {
    bands: Object,    // Projection::BANDS
    presets: Object,  // RiskQuiz::PRESETS
    assets: Object,   // asset key -> bag (null for a mix)
    waits: Array,     // DcaCalculator::WAIT_YEARS
    minimum: Number,  // InvestmentPlan::MINIMUM_MONTHLY_USDC
    onboardingUrl: String
  }

  // With JavaScript the figures are live; the form's submit is only the
  // no-JavaScript path.
  submit(event) {
    event.preventDefault()
    this.update()
  }

  update() {
    const asset = this.checked("asset") || "mix"
    const mix = this.checked("mix") || "balanced"
    const amount = Number(this.amountTarget.value)
    const years = Number(this.yearsTarget.value)
    const monthly = amount
    const split = this.split(asset, mix)

    // Controls read back.
    this.amountOutTarget.textContent = dollars(amount)
    this.amountTarget.setAttribute("aria-valuetext", `${dollars(amount)} a month`)
    this.yearsOutTarget.textContent = years
    this.yearsUnitTarget.textContent = years === 1 ? "year" : "years"
    this.yearsTarget.setAttribute("aria-valuetext", `${years} ${years === 1 ? "year" : "years"}`)
    this.mixGroupTarget.hidden = asset !== "mix"
    const preset = this.presetsValue[mix]
    this.mixWordsTarget.textContent = `${preset.gold}% gold, ${preset.stocks}% S&P 500, ${preset.crypto}% Solana`
    const label = this.element.querySelector(`label[for="dca-asset-${asset}"]`)?.textContent.trim()
    this.assetNameTarget.textContent = asset === "mix" ? `a ${mix} mix` : label

    // The result: the middle of the range leads, the range sits beside it.
    const result = this.project(monthly, years, split)
    this.midTarget.textContent = roundedDollars(result.mid)
    this.lowTarget.textContent = roundedDollars(result.low)
    this.highTarget.textContent = roundedDollars(result.high)
    this.contributedTarget.textContent = roundedDollars(result.contributed)
    const growth = result.contributed > 0 ? ((result.mid - result.contributed) / result.contributed) * 100 : 0
    this.pctTarget.textContent = growth >= 1 ? `+${Math.round(growth)}%` : ""
    const putIn = result.mid > 0 ? Math.min(100, (result.contributed / result.mid) * 100) : 100
    this.putInBarTarget.style.width = `${putIn}%`
    this.growthBarTarget.style.width = `${100 - putIn}%`

    // The cost of waiting: same end date, later starts.
    this.waitYearsTarget.textContent = `${years} ${years === 1 ? "year" : "years"}`
    this.waitTodayTarget.textContent = roundedDollars(result.mid)
    this.waitsValue.forEach((wait, i) => {
      const possible = years > wait
      const later = this.project(monthly, years - wait, split)
      this.waitMidTargets[i].textContent = possible ? roundedDollars(later.mid) : "—"
      this.waitLostTargets[i].textContent = possible ? roundedDollars(-(result.mid - later.mid)) : "too late"
    })

    // The plan builder, opened on these values (OnboardingController#plan).
    const planAmount = Math.max(Math.round(monthly), this.minimumValue)
    const cta = new URLSearchParams({ amount: planAmount, gold: split.gold, stocks: split.stocks, crypto: split.crypto })
    this.ctaTarget.href = `${this.onboardingUrlValue}?${cta}`
    this.ctaNoteTarget.hidden = Math.round(monthly) >= this.minimumValue

    // The shareable address: only what differs from the defaults' shape
    // (DcaCalculator#to_query_params).
    const query = new URLSearchParams({ amount, years, asset })
    if (asset === "mix") query.set("mix", mix)
    history.replaceState(history.state, "", `${window.location.pathname}?${query}`)
  }

  split(asset, mix) {
    const bag = this.assetsValue[asset]
    if (!bag) return this.presetsValue[mix]
    return { gold: 0, stocks: 0, crypto: 0, [bag]: 100 }
  }

  project(amount, years, split) {
    if (years <= 0) return { low: 0, high: 0, mid: 0, contributed: 0 }
    return projectRange({ amount, months: years * 12, bands: this.bandsValue, split, denom: 100 })
  }

  checked(name) {
    return this.element.querySelector(`input[name="${name}"]:checked`)?.value
  }
}
